import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getLocale,
  getLocaleDir,
  setLocale as persistLocale,
  t as translate,
  type Locale,
  type TranslationParams,
} from '@/lib/i18n';

interface LocaleContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (locale: Locale) => void;
  /** Cambio en tiempo real — sin reload. Params: `{level: 5}` reemplaza `{level}` */
  t: (key: string, params?: TranslationParams) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function applyDocumentLocale(locale: Locale): void {
  const dir = getLocaleDir(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = dir;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getLocale);

  const setLocale = (next: Locale) => {
    persistLocale(next);
    setLocaleState(next);
    applyDocumentLocale(next);
  };

  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      dir: getLocaleDir(locale),
      setLocale,
      t: (key: string, params?: TranslationParams) => translate(key, locale, params),
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    const locale = getLocale();
    return {
      locale,
      dir: getLocaleDir(locale),
      setLocale: persistLocale,
      t: (key: string, params?: TranslationParams) => translate(key, locale, params),
    };
  }
  return ctx;
}
